import { FieldProps, FormattedTextField, LinkField, useFieldHelper } from 'framework/ui/field';
import { EXTERNAL_USER_PREFIX } from './RiskOwnerDropdown';
import {
  useEmployeeUrl,
  useEmployeeNamesService,
  useEmployeeService,
  useIsEmployeeProfileAccessible,
} from 'domain/shared/employee';
import { EMPTY_VALUE_INDICATOR, useAsyncValue } from 'framework/utils';
import { LoadingDecorator } from 'framework/ui/ui-builder';
import { Tooltip } from 'framework/ui/mui-builder';
import { Typography } from '@mui/material';

const RiskOwnerField = ({ source, ...props }: FieldProps) => {
  const { getValue } = useFieldHelper({ source });
  const nameOrAlias = getValue();
  const isExternal = !!nameOrAlias && nameOrAlias.startsWith(EXTERNAL_USER_PREFIX);

  const { getProfileUrl } = useEmployeeUrl();
  const { getFullName } = useEmployeeService();
  const { getEmployeeNamesByAliases } = useEmployeeNamesService();
  const isOwnerProfileAccessible = useIsEmployeeProfileAccessible(isExternal ? null : nameOrAlias);

  const employeeName = useAsyncValue(async () => {
    if (!nameOrAlias) {
      return '';
    }

    if (isExternal) {
      return nameOrAlias.slice(EXTERNAL_USER_PREFIX.length);
    } else {
      const employeeNames = await getEmployeeNamesByAliases([nameOrAlias]);
      return employeeNames[0] ? getFullName(employeeNames[0].nameEn) : '';
    }
  }, [nameOrAlias, isExternal]);

  if (!nameOrAlias) {
    return <>{EMPTY_VALUE_INDICATOR}</>;
  }

  const content = () => {
    if (!employeeName) {
      return <></>;
    } else if (isExternal || !isOwnerProfileAccessible) {
      return (
        <FormattedTextField
          {...props}
          source={source}
          value={employeeName}
          format={(str: string) =>
            str && str.length > 60 ? (
              <Tooltip title={str}>
                <div>
                  <Typography
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: 1.2,
                      wordBreak: 'break-word',
                    }}
                  >
                    {str}
                  </Typography>
                </div>
              </Tooltip>
            ) : (
              str
            )
          }
        />
      );
    } else {
      return (
        <LinkField {...props} isInternal source={source} value={getProfileUrl(nameOrAlias)} linkText={employeeName} />
      );
    }
  };

  return (
    <LoadingDecorator loading={!employeeName} isSmallLoader deps={[nameOrAlias]}>
      {content()}
    </LoadingDecorator>
  );
};

export default RiskOwnerField;
